import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

import { StatusChipProps, StatusChipVariant } from '@/components/ui/StatusChip';
import type { Agent } from '@/hooks/useAgents';
import { AGENT_THRESHOLDS } from '@/sections/agents/constants';
import {
  agentStatusVariants,
  getVariantForStatus,
} from '@/utils/statusChip.util';

/**
 * Extract shared connection detection logic
 *
 * Determines if agent is connected based on last_seen_at timestamp
 * and configured online threshold (5 minutes).
 *
 * @param agent - The agent to check
 * @returns Connection info with isConnected flag and time difference
 */
export const getAgentConnectionInfo = (agent: Agent) => {
  if (!agent.last_seen_at) {
    return { isConnected: false, diffInMinutes: Infinity };
  }

  const isoString = new Date(
    Math.floor(Number(agent.last_seen_at) / 1000)
  ).toISOString();

  const now = new Date();
  const visitedDate = new Date(isoString);
  const diffInMinutes = (now.getTime() - visitedDate.getTime()) / (1000 * 60);

  const isConnected = diffInMinutes < AGENT_THRESHOLDS.ONLINE_MINUTES;

  return { isConnected, diffInMinutes };
};

interface AgentStatusChipProps extends StatusChipProps {
  variant: StatusChipVariant;
}

/**
 * Get agent status properties for display
 *
 * Simplified to show only "Online" or "Offline" based on connectivity.
 * Detailed health information is available in the Health tab.
 *
 * @param agent - The agent to get status for
 * @returns Status properties including label, variant, icon, and tooltip
 */
export const getAgentStatusProps = (
  agent: Agent
): AgentStatusChipProps | undefined => {
  if (!agent.last_seen_at) return undefined;

  const { isConnected } = getAgentConnectionInfo(agent);

  // Offline agents (disconnected)
  if (!isConnected) {
    return {
      label: 'Offline',
      variant: getVariantForStatus('offline', agentStatusVariants),
      icon: <XCircleIcon className="size-3 shrink-0" />,
      tooltip: `Agent has not been seen in over ${AGENT_THRESHOLDS.ONLINE_MINUTES} minutes`,
    };
  }

  // Online agents - simplified status (health details in Health tab)
  return {
    label: 'Online',
    variant: getVariantForStatus('online', agentStatusVariants),
    icon: <CheckCircleIcon className="size-3 shrink-0" />,
    tooltip: 'Agent is online',
  };
};
